import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton
} from '@invent/wl-ui-kit-next/ui';
import { Plus, Trash2 } from 'lucide-react';
import * as React from 'react';

import { NotPermitted } from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { definitionsApi } from '../api/definitions-api';
import type {
  IOptionListItem,
  IOptionListItemWrite,
  IOptionListRow,
  IOptionListVersionDetail
} from '../api/definitions-contracts';
import {
  AdminPage,
  LoadError,
  BUILDER_TABS,
  useAdminData,
  useDebounced
} from '../components/admin-page';
import { refusalMessage } from '../refusal';

/**
 * Option lists and their versions — where cross-survey drift is visible, and where the
 * freeze rule is most easily mistaken for a bug. Real as of SR-123 ticket 3.
 *
 * Two model facts drive the whole screen:
 *
 * - **Membership is versioned, identity is not.** Rules and stored answers reference
 *   `option.code`, never a label, so an option is dropped from a later version's membership
 *   rather than deleted. That is why removing a row here only affects the version being
 *   edited.
 * - **A published version is frozen.** `OptionListVersion` rejects writes once published
 *   (design §3.3), so the editor is available on drafts only, and changing a published list
 *   means cutting a new draft from it.
 *
 * ## Two reads, not one
 *
 * The catalogue (`GET /option-lists`) carries version rows and an `itemCount` but **no
 * membership** — deliberately, because the membership read is the one whose entries are legal
 * `PUT …/items` bodies. So the left column comes from the catalogue and the editor fetches
 * the selected list's versions on demand, which also keeps a hundred lists from dragging
 * their items along.
 *
 * The filter is `?code=`, a **prefix** — the only filter this endpoint has.
 */
export function AdminOptionListsPage() {
  const [search, setSearch] = React.useState('');
  const settled = useDebounced(search);
  const [selectedCode, setSelectedCode] = React.useState<Nullable<string>>(null);
  const [creating, setCreating] = React.useState(false);

  const { data: lists, error, setError, reload } = useAdminData<IOptionListRow[]>(
    (signal) => definitionsApi.optionLists(settled.trim() || undefined, signal),
    [settled]
  );

  const selected =
    (lists ?? []).find((list) => list.code === selectedCode) ?? (lists ?? [])[0] ?? null;

  return (
    <AdminPage title="Survey builder" tabs={BUILDER_TABS}>
      {error && <LoadError message={error} />}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          placeholder="Code starts with…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-xs"
          aria-label="Filter by code prefix"
        />
        <span className="text-sm text-muted-foreground">{lists?.length ?? 0} lists</span>
        <Button className="ml-auto" onClick={() => setCreating(true)}>
          <Plus />
          New list
        </Button>
      </div>

      {creating && (
        <CreateList
          onCreated={(code) => {
            setCreating(false);
            setSelectedCode(code);
            void reload();
          }}
          onCancel={() => setCreating(false)}
          onFailed={setError}
        />
      )}

      {lists === null ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
          <nav
            aria-label="Option lists"
            className="grid max-h-[36rem] content-start gap-1 overflow-y-auto"
          >
            {lists.map((list) => (
              <button
                key={list.optionListId}
                type="button"
                onClick={() => setSelectedCode(list.code)}
                className={
                  'rounded-md px-3 py-2 text-left text-sm transition-colors ' +
                  (selected?.code === list.code
                    ? 'bg-[var(--color-slight-blue)] font-medium text-[var(--color-royal-blue)]'
                    : 'hover:bg-[var(--color-slight-blue)]/60')
                }
              >
                <span className="block font-mono text-xs">{list.code}</span>
                <span className="block text-xs text-muted-foreground">
                  {list.name} · {list.versions.length} version
                  {list.versions.length === 1 ? '' : 's'}
                </span>
              </button>
            ))}
            {lists.length === 0 && (
              <p className="px-3 py-6 text-sm text-muted-foreground">
                No list code starts with that.
              </p>
            )}
          </nav>

          {selected && (
            <OptionListDetail
              key={selected.code}
              list={selected}
              onChanged={() => void reload()}
              onFailed={setError}
            />
          )}
        </div>
      )}
    </AdminPage>
  );
}

function CreateList({
  onCreated,
  onCancel,
  onFailed
}: {
  onCreated: (code: string) => void;
  onCancel: () => void;
  onFailed: (message: string) => void;
}) {
  const [code, setCode] = React.useState('');
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const created = await definitionsApi.createOptionList({ code: code.trim(), name });
      onCreated(created.code);
    } catch (createError) {
      onFailed(refusalMessage(createError, 'Could not create this list'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">New option list</CardTitle>
        <CardDescription>
          Creates the list row only — it has no versions until one is cut, the same shape a
          new survey has.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="flex flex-wrap items-center gap-3" onSubmit={submit}>
          <Input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="list_code"
            aria-label="List code"
            className="max-w-xs font-mono"
          />
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Human name"
            aria-label="List name"
            className="max-w-xs"
          />
          <Button type="submit" disabled={busy || code.trim() === '' || name.trim() === ''}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * One list: its versions, and the membership of whichever is selected.
 *
 * The caller's `key={selected.code}` is what resets this component when a different list is
 * picked — otherwise a version number from the previous list would survive into a list that
 * has no such version.
 */
function OptionListDetail({
  list,
  onChanged,
  onFailed
}: {
  list: IOptionListRow;
  onChanged: () => void;
  onFailed: (message: string) => void;
}) {
  const [picked, setPicked] = React.useState<Nullable<number>>(null);
  const [busy, setBusy] = React.useState(false);
  // Publish and Retire are `definitions.publish` (SR-228), apart from the authoring the screen
  // opens on: drawn off with the reason.
  const canPublish = useCan()('definitions.publish');
  const publishReason = React.useId();

  const { data: versions, reload } = useAdminData<IOptionListVersionDetail[]>(
    (signal) => definitionsApi.optionListVersions(list.code, signal),
    [list.code]
  );

  // Newest first when nothing is picked: the version an author wants is almost always the
  // latest one, and on a list with a draft that is the draft.
  const selected =
    (versions ?? []).find((entry) => entry.version.versionNo === picked) ??
    [...(versions ?? [])].sort((a, b) => b.version.versionNo - a.version.versionNo)[0] ??
    null;

  const hasDraft = (versions ?? []).some((entry) => entry.version.status === 'draft');

  const act = async (run: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    try {
      await run();
      setPicked(null);
      await reload();
      onChanged();
    } catch (actError) {
      onFailed(refusalMessage(actError, fallback));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="font-mono text-base">{list.code}</CardTitle>
          <CardDescription>{list.name}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="grid gap-1 text-sm">
              <span className="text-xs tracking-wide text-muted-foreground uppercase">
                Version
              </span>
              <Select
                value={String(selected?.version.versionNo ?? '')}
                onValueChange={(next) => setPicked(Number(next))}
              >
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(versions ?? []).map((entry) => (
                    <SelectItem key={entry.version.olvId} value={String(entry.version.versionNo)}>
                      v{entry.version.versionNo} · {entry.version.locale} ·{' '}
                      {entry.version.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <Button
              variant="outline"
              disabled={busy || hasDraft}
              onClick={() =>
                void act(
                  () =>
                    definitionsApi.createOptionListVersion(list.code, {
                      // Clone the version on screen when there is one, so a new draft starts
                      // from the membership an author was just looking at rather than empty.
                      source: selected ? 'clone' : 'empty',
                      fromVersion: selected?.version.versionNo
                    }),
                  'Could not cut a draft'
                )
              }
            >
              <Plus />
              {hasDraft ? 'Draft exists' : 'Cut a new draft'}
            </Button>

            {selected?.version.status === 'draft' && (
              <>
                <Button
                  disabled={busy || !canPublish}
                  aria-describedby={canPublish ? undefined : publishReason}
                  onClick={() =>
                    void act(
                      () =>
                        definitionsApi.publishOptionListVersion(
                          list.code,
                          selected.version.versionNo
                        ),
                      'Could not publish this version'
                    )
                  }
                >
                  Publish
                </Button>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() =>
                    void act(
                      () =>
                        definitionsApi.deleteOptionListVersion(
                          list.code,
                          selected.version.versionNo
                        ),
                      'Could not discard this draft'
                    )
                  }
                >
                  Discard draft
                </Button>
              </>
            )}

            {selected?.version.status === 'published' && (
              <Button
                variant="outline"
                disabled={busy || !canPublish}
                aria-describedby={canPublish ? undefined : publishReason}
                onClick={() =>
                  void act(
                    () =>
                      definitionsApi.retireOptionListVersion(
                        list.code,
                        selected.version.versionNo
                      ),
                    'Could not retire this version'
                  )
                }
              >
                Retire
              </Button>
            )}
          </div>
          {!canPublish &&
            (selected?.version.status === 'draft' || selected?.version.status === 'published') && (
              <NotPermitted id={publishReason} what="publishing or retiring a version" />
            )}

          {selected && (
            <p className="text-xs text-muted-foreground">
              {selected.version.status === 'published'
                ? `Published${
                    selected.version.publishedAt
                      ? ' ' + formatDateTime(selected.version.publishedAt)
                      : ''
                  }. This is the version an unpinned usage resolves to, and it is frozen.`
                : selected.version.status === 'retired'
                  ? 'Retired — frozen, and kept because stored answers still reference its codes.'
                  : 'Draft. The only state whose membership can change.'}
            </p>
          )}
        </CardContent>
      </Card>

      {selected && (
        <ItemsEditor
          key={selected.version.olvId}
          listCode={list.code}
          detail={selected}
          onSaved={() => {
            setPicked(null);
            void reload();
            onChanged();
          }}
          onFailed={onFailed}
        />
      )}
    </div>
  );
}

/**
 * The membership of one list version.
 *
 * Edited as a whole and saved in one `PUT`: the server numbers the array from the order it
 * arrives in, so there is no per-item write and no ordinal to send. The editor therefore
 * holds its own copy until Save — moving or removing a row is not a request, which is what
 * makes reordering usable at all.
 */
function ItemsEditor({
  listCode,
  detail,
  onSaved,
  onFailed
}: {
  listCode: string;
  detail: IOptionListVersionDetail;
  onSaved: () => void;
  onFailed: (message: string) => void;
}) {
  const frozen = detail.version.status !== 'draft';
  const committed = React.useMemo(() => detail.items.map(toWrite), [detail.items]);
  const [items, setItems] = React.useState<IOptionListItemWrite[]>(committed);
  const [saving, setSaving] = React.useState(false);

  const dirty = JSON.stringify(items) !== JSON.stringify(committed);

  const patch = (index: number, change: Partial<IOptionListItemWrite>) =>
    setItems((current) =>
      current.map((item, at) => (at === index ? { ...item, ...change } : item))
    );

  const move = (index: number, by: number) =>
    setItems((current) => {
      const target = index + by;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      await definitionsApi.setOptionListItems(listCode, detail.version.versionNo, items);
      onSaved();
    } catch (saveError) {
      onFailed(refusalMessage(saveError, 'Could not save this membership'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Options · v{detail.version.versionNo}</CardTitle>
        <CardDescription>
          {frozen
            ? 'Read-only — this version is frozen.'
            : 'The whole membership is replaced on save, in the order shown.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {frozen && (
          <Alert>
            <AlertTitle>Frozen</AlertTitle>
            <AlertDescription>
              A published or retired <code>option_list_version</code> refuses writes in the
              domain aggregate, so the server would answer 409. Cut a draft to change this.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-2">
          {items.map((item, index) => (
            <div
              key={index}
              className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2"
            >
              <span className="w-6 text-xs text-muted-foreground">{index + 1}</span>
              <Input
                value={item.code}
                onChange={(event) => patch(index, { code: event.target.value })}
                disabled={frozen}
                aria-label={`Option ${index + 1} code`}
                className="max-w-[14rem] font-mono text-xs"
              />
              <Input
                value={item.label}
                onChange={(event) => patch(index, { label: event.target.value })}
                disabled={frozen}
                aria-label={`Option ${index + 1} label`}
                className="max-w-sm"
              />
              <label className="flex items-center gap-1 text-xs text-muted-foreground">
                <Checkbox
                  checked={item.isExclusive}
                  onCheckedChange={(next) => patch(index, { isExclusive: next === true })}
                  disabled={frozen}
                />
                exclusive
              </label>
              {!frozen && (
                <span className="ml-auto flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`Move option ${index + 1} up`}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`Move option ${index + 1} down`}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`Remove option ${index + 1}`}
                    onClick={() => setItems((c) => c.filter((_, at) => at !== index))}
                  >
                    <Trash2 />
                  </Button>
                </span>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No options in this version.
            </p>
          )}
        </div>

        {!frozen && (
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() =>
                setItems((current) => [
                  ...current,
                  { code: '', label: '', isExclusive: false, meta: null }
                ])
              }
            >
              <Plus />
              Add option
            </Button>
            <Button onClick={() => void save()} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save membership'}
            </Button>
            {!dirty && <span className="text-sm text-muted-foreground">No changes.</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** The read shape minus `ordinal`, which the API owns. This is the one place on the
 *  authoring surface where a read document is *not* a legal write body verbatim, and the
 *  server's own docs record it. */
function toWrite(item: IOptionListItem): IOptionListItemWrite {
  return {
    code: item.code,
    label: item.label,
    isExclusive: item.isExclusive,
    meta: item.meta
  };
}
